import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Container, Typography, Box, Paper,
  FormControl, InputLabel, Select, MenuItem, Button
} from "@mui/material";
import AuthHeader from "../components/AuthHeader";
import { useAuth } from "../contexts/AuthContext";

const PronunciationLabPage = ({ API_BASE, AUDIO_BASE }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();



  // 週選択とカード表示の状態
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const stateWeek = location.state?.selectedWeek;
    return stateWeek ?? "";
  });
  const [sentences, setSentences] = useState([]);
  const [selectedSentence, setSelectedSentence] = useState(null);

  // 音声再生関連の状態
  const [playingKey, setPlayingKey] = useState(null);
  const [currentPlayLog, setCurrentPlayLog] = useState(null);
  const audioRefs = useRef({});

  // 音声再生ログ開始
  const startAudioPlayLog = async (sentence, audioFileName) => {
    if (!user) return;

    // db_user_idが無い場合はログアウトしてログイン画面へリダイレクト
    if (!user.db_user_id) {
      console.warn('⚠️ db_user_idが無いため、ログアウトしてログイン画面へリダイレクトします。');
      logout();
      return;
    }

    try {
      const logData = {
        session_id: String(user.db_user_id),
        content_type: 'pronunciation_lab_sample',
        content_id: sentence.id,
        audio_file_name: audioFileName,
        is_video: 0,
        play_start_time: new Date().toISOString(),
        playback_rate: 1.0 // お手本再生は等速
      };

      const response = await fetch(`${API_BASE}/audio_play_logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData)
      });

      const data = await response.json();
      if (data.success) {
        setCurrentPlayLog({
          log_id: data.log_id,
          start_time: new Date(),
          ...logData
        });
      }
    } catch (error) {
      console.error('音声ログ開始エラー:', error);
    }
  };

  // 音声再生ログ完了
  const completeCurrentPlayLog = async () => {
    if (!currentPlayLog) return;

    try {
      const endTime = new Date();
      const durationSeconds = (endTime - currentPlayLog.start_time) / 1000;

      const updateData = {
        ...currentPlayLog,
        play_end_time: endTime.toISOString(),
        duration_seconds: durationSeconds
      };

      await fetch(`${API_BASE}/audio_play_logs/${currentPlayLog.log_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      setCurrentPlayLog(null);
    } catch (error) {
      console.error('音声ログ完了エラー:', error);
    }
  };

  // 録音機能の状態
  const [isCountdown, setIsCountdown] = useState(false);
  const [countdownValue, setCountdownValue] = useState(4);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [attemptStatuses, setAttemptStatuses] = useState({});

  // 除外する単語のセット
  const EXCLUDED_WORDS = new Set([
    'a', 'an', 'the',
    'is', 'am', 'are', 'was',
    'to', 'in', 'on', 'at', 'of', 'for',
    'but', 'and', 'or', 'so', 'if', 'as',
  ]);
  const normalizeWord = (word = "") =>
    word
      .toString()
      .replace(/[’`]/g, "'")
      .toLowerCase()
      .trim()
      .replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, "");

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const countdownTimerRef = useRef(null);

  // 週一覧を取得
  useEffect(() => {
    fetch(`${API_BASE}/transcription_weeks`)
      .then((res) => res.json())
      .then((data) => {
        const filtered = Array.isArray(data)
          ? data.filter((w) => w.category === "ExampleSent").map((w) => w.week_num)
          : [];
        const reversed = [...filtered].reverse();
        setWeekOptions(reversed);
        if (reversed.length > 0) {
          setSelectedWeek(prev => {
            if (prev && reversed.includes(prev)) {
              return prev;
            }
            return reversed[0];
          });
        }
      })
      .catch((err) => console.error("week fetch error:", err));
  }, [API_BASE]);

  // 選択された週の文を取得
  useEffect(() => {
    if (selectedWeek) {
      fetch(`${API_BASE}/recognized_sentences_by_week/${selectedWeek}`)
        .then((res) => res.json())
        .then((data) => {
          const sentenceData = Array.isArray(data) ? data : [];
          setSentences(sentenceData);
        })
        .catch((err) => console.error("API fetch error:", err));
    }
  }, [selectedWeek, API_BASE]);

  useEffect(() => {
    if (!user?.db_user_id) {
      setAttemptStatuses({});
      return;
    }
    if (!Array.isArray(sentences) || sentences.length === 0) {
      setAttemptStatuses({});
      return;
    }
    const recognizedIds = sentences
      .map((sentence) => sentence?.id)
      .filter((id) => id !== undefined && id !== null);
    if (recognizedIds.length === 0) {
      setAttemptStatuses({});
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    fetch(`${API_BASE}/pronunciation/attempt-status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: user.db_user_id,
        recognized_sentence_ids: recognizedIds,
      }),
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const next = {};
        if (Array.isArray(data?.statuses)) {
          data.statuses.forEach((item) => {
            if (
              item &&
              item.recognized_sentence_id !== undefined &&
              item.recognized_sentence_id !== null
            ) {
              next[item.recognized_sentence_id] = Boolean(item.passed);
            }
          });
        }
        setAttemptStatuses(next);
      })
      .catch((err) => {
        if (cancelled || err.name === "AbortError") {
          return;
        }
        console.error("合否サマリー取得エラー:", err);
        setAttemptStatuses({});
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [API_BASE, sentences, user?.db_user_id]);

  const attemptSummaryRows = useMemo(() => {
    if (!Array.isArray(sentences)) {
      return [];
    }
    const map = new Map();
    sentences.forEach((sentence) => {
      if (sentence?.sentence_index === undefined || sentence?.sentence_index === null) {
        return;
      }
      const indexKey = Number(sentence.sentence_index);
      const passed = Boolean(attemptStatuses[sentence.id]);
      if (passed) {
        map.set(indexKey, true);
      } else if (!map.has(indexKey)) {
        map.set(indexKey, false);
      }
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([index, passed]) => ({ index, passed }));
  }, [sentences, attemptStatuses]);

  const startCountdown = async () => {
    // マイク使用許可を求める
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // カウントダウン開始
      setIsCountdown(true);
      setCountdownValue(4);
      setError(null);
      setAnalysisResult(null);

      let count = 4;
      countdownTimerRef.current = setInterval(() => {
        count -= 1;
        setCountdownValue(count);

        if (count <= 0) {
          clearInterval(countdownTimerRef.current);
          setIsCountdown(false);
          startRecordingAfterCountdown(stream);
        }
      }, 1000);

    } catch (err) {
      console.error("マイクアクセスエラー:", err);
      setError("マイクへのアクセスが許可されませんでした。");
    }
  };

  const cancelCountdown = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setIsCountdown(false);
    setCountdownValue(4);

    // ストリームを停止
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const startRecordingAfterCountdown = (stream) => {
    // MediaRecorderのセットアップ
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    audioChunksRef.current = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
      await sendAudioToServer(audioBlob);

      // ストリームを停止
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };

    mediaRecorder.start();
    setIsRecording(true);
    setRecordingTime(0);
    setHasRecorded(false);

    // タイマーの開始
    timerRef.current = setInterval(() => {
      setRecordingTime((prevTime) => {
        const newTime = prevTime + 1;
        if (newTime >= 7) {
          stopRecording();
          return 7;
        }
        return newTime;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setHasRecorded(true);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const sendAudioToServer = async (audioBlob) => {
    setIsAnalyzing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");

      formData.append("activity_type", "lab_practice");

      if (user?.db_user_id !== undefined && user?.db_user_id !== null) {
        formData.append("user_id", String(user.db_user_id));
      }

      if (typeof recordingTime === "number" && !Number.isNaN(recordingTime)) {
        formData.append("recording_seconds", String(recordingTime));
      }

      if (selectedWeek !== undefined && selectedWeek !== null && selectedWeek !== "") {
        formData.append("source_week", String(selectedWeek));
      }

      formData.append("lesson_flow_id", "pronunciation-lab");

      // 選択された文がある場合は参照テキストとして送信
      if (selectedSentence && selectedSentence.sentence) {
        formData.append("reference_text", selectedSentence.sentence);
      }
      if (selectedSentence?.id) {
        formData.append("recognized_sentence_id", String(selectedSentence.id));
      }

      const apiUrl = API_BASE ? `${API_BASE}/pronunciation/analyze` : "/api/pronunciation/analyze";
      const response = await fetch(apiUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`サーバーエラー: ${response.status}`);
      }

      const result = await response.json();
      if (result.no_speech_detected) {
        if (result?.warning) {
          setError(result.warning);
        }
        setShowDetailedView(false);
        setSelectedSentence(selectedSentence); // stay on current sentence
        return;
      }
      const nextResult = { ...result };
      if (result.logging_error) {
        setError("分析結果の保存に失敗しました。時間をおいて再度お試しください。");
      }
      setAnalysisResult(nextResult);
    } catch (err) {
      console.error("音声送信エラー:", err);
      setError("音声の分析中にエラーが発生しました。");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const resetRecording = () => {
    setIsRecording(false);
    setRecordingTime(0);
    setHasRecorded(false);
    setAnalysisResult(null);
    setError(null);
    setShowDetailedView(false);
    audioChunksRef.current = [];
  };

  const confidenceLookup = useMemo(() => {
    const bySourceId = new Map();
    const byNormalized = new Map();
    if (Array.isArray(analysisResult?.words)) {
      analysisResult.words.forEach((word) => {
        if (!word || word.ignore_for_scoring) {
          return;
        }
        if (typeof word.confidence !== "number") {
          return;
        }
        if (word.source_token_id !== undefined && word.source_token_id !== null) {
          const sourceKey = String(word.source_token_id);
          if (!bySourceId.has(sourceKey)) {
            bySourceId.set(sourceKey, word.confidence);
          }
        }
        const normalizedKey =
          typeof word.normalized === "string" && word.normalized
            ? word.normalized
            : normalizeWord(word.word ?? "");
        if (normalizedKey && !byNormalized.has(normalizedKey)) {
          byNormalized.set(normalizedKey, word.confidence);
        }
      });
    }
    return { bySourceId, byNormalized };
  }, [analysisResult]);

  const resolveConfidence = (alignmentItem) => {
    if (!alignmentItem || !Array.isArray(analysisResult?.words)) {
      return null;
    }
    const { bySourceId, byNormalized } = confidenceLookup;
    const sourceId = alignmentItem.source_token_id;
    if (sourceId !== undefined && sourceId !== null) {
      const key = String(sourceId);
      if (bySourceId.has(key)) {
        return bySourceId.get(key);
      }
    }
    const normalizedKey =
      typeof alignmentItem.normalized === "string" && alignmentItem.normalized
        ? alignmentItem.normalized
        : normalizeWord(alignmentItem.hyp_word ?? "");
    if (normalizedKey && byNormalized.has(normalizedKey)) {
      return byNormalized.get(normalizedKey);
    }
    if (normalizedKey) {
      const fallback = analysisResult.words.find(
        (word) =>
          word &&
          !word.ignore_for_scoring &&
          normalizeWord(word.word ?? "") === normalizedKey
      );
      if (fallback && typeof fallback.confidence === "number") {
        return fallback.confidence;
      }
    }
    if (typeof alignmentItem?.confidence === "number") {
      return alignmentItem.confidence;
    }
    return null;
  };

  // 合格判定関数
  const checkPassStatus = () => {
    if (!analysisResult || !analysisResult.alignment || !analysisResult.alignment.alignments) {
      return false;
    }

    const alignments = analysisResult.alignment.alignments || [];
    const filteredAlignments = alignments.filter(item => !item?.ignore_for_scoring);

    // すべての単語が完全一致か確認
    const allMatch = filteredAlignments.every(item => item.status === "match");
    if (!allMatch) {
      return false;
    }

    // すべての単語の発音信頼度が0.4以上か確認（EXCLUDED_WORDSは除外）
    for (const item of filteredAlignments) {
      if (!item.hyp_word) {
        continue;
      }
      const normalizedHyp =
        typeof item.normalized === "string" && item.normalized
          ? item.normalized
          : normalizeWord(item.hyp_word);
      if (!normalizedHyp || EXCLUDED_WORDS.has(normalizedHyp)) {
        continue;
      }

      const confidence = resolveConfidence(item);
      if (confidence !== null && confidence < 0.4) {
        return false;
      }
    }

    return true;
  };

  // 評価テキストを取得する関数
  const getEvaluationText = (item) => {
    if (item?.is_proper_noun || item?.ignore_for_scoring || item?.ignored_due_to_proper) {
      if (item?.is_proper_noun) {
        return "採点外";
      }
      return "";
    }
    if (item.status === "deletion") {
      return "聞き取れませんでした";
    }
    if (item.status === "insertion" || item.status === "error") {
      return "間違いです";
    }
    if (item.status === "match") {
      const normalizedHyp =
        typeof item.normalized === "string" && item.normalized
          ? item.normalized
          : normalizeWord(item.hyp_word ?? "");
      let confidence = resolveConfidence(item);

      if (normalizedHyp && EXCLUDED_WORDS.has(normalizedHyp)) {
        confidence = Math.max(confidence ?? 0, 0.6);
      }

      if (confidence !== null) {
        if (confidence >= 0.6) {
          return "完璧";
        } else if (confidence >= 0.4) {
          return "上手";
        } else {
          return "聞き取りにくいです";
        }
      }
    }
    return "-";
  };

  // 音声再生関連の関数
  const stopAllAudio = async () => {
    if (currentPlayLog) {
      await completeCurrentPlayLog();
    }
    Object.values(audioRefs.current).forEach((audio) => {
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    setPlayingKey(null);
  };

  const handlePlay = async (index, audioIndex = 0) => {
    await stopAllAudio();

    const key = `${index}_${audioIndex}`;
    const audio = audioRefs.current[key];
    const sentence = sentences[index];
    const audioFile = sentence?.audio_files[audioIndex];

    if (audio && sentence && audioFile) {
      await startAudioPlayLog(sentence, audioFile);
      audio.play();
      setPlayingKey(key);
    }
  };

  const handleEnded = async () => {
    await completeCurrentPlayLog();
    setPlayingKey(null);
  };
  const handleNavigateToLesson = (sentence) => {
    navigate('/pronunciation_lesson', {
      state: {
        fromWeek: selectedWeek || null,
        lesson: {
          lesson_flow_id: "pronunciation-lesson",
          recognized_sentence_id: sentence.id,
          sentence_en: sentence.sentence,
          sentence_jp: sentence.sentence_jp,
          audio_file: sentence.audio_files[0] ?? ""
        }
      }
    });
  };

  const handleStartExam = () => {
    if (!Array.isArray(sentences) || sentences.length === 0) {
      setError("出題できる例文が見つかりません。");
      return;
    }

    const remaining = sentences.filter((sentence) => !attemptStatuses[sentence.id]);
    const pool = remaining.length > 0 ? remaining : sentences;
    const randomSentence = pool[Math.floor(Math.random() * pool.length)];
    if (!randomSentence) {
      setError("出題できる例文が見つかりません。");
      return;
    }
    setError(null);
    navigate('/pronunciation_lesson', {
      state: {
        fromWeek: selectedWeek || null,
        lesson: {
          lesson_flow_id: "pronunciation-exam",
          is_exam: true,
          recognized_sentence_id: randomSentence.id,
          sentence_en: randomSentence.sentence,
          sentence_jp: randomSentence.sentence_jp,
          audio_file: ""
        }
      }
    });
  };

  const handlePracticeInPage = (sentence) => {
    setSelectedSentence(sentence);
    resetRecording();
  };

  const handleBackToList = () => {
    setSelectedSentence(null);
    resetRecording();
  };

  // クリーンアップ
  useEffect(() => {
    return () => {
      // コンポーネントがアンマウントされるときにログを完了させる
      if (currentPlayLog) {
        completeCurrentPlayLog();
      }
    };
  }, [currentPlayLog]);



  // 週選択とカード一覧の表示
  if (!selectedSentence) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
        <AuthHeader pageTitle="Pronunciation Lab" />
        <Container maxWidth="md" sx={{ paddingTop: 3, paddingBottom: 3 }}>
          {/* 週選択プルダウン */}
          <Box sx={{ marginBottom: 4 }}>
          <FormControl fullWidth>
            <InputLabel id="week-label">週番号を選択</InputLabel>
            <Select
              labelId="week-label"
              value={selectedWeek}
              label="週番号を選択"
              onChange={(e) => setSelectedWeek(e.target.value)}
            >
              {weekOptions.map((week, i) => (
                <MenuItem key={i} value={week}>
                  {week}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ marginBottom: 3 }}>
          <Paper
            elevation={1}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "stretch",
              gap: 2,
              padding: { xs: 2.5, md: 3 },
              background: "linear-gradient(140deg, rgba(14,165,233,0.12), rgba(59,130,246,0.16))",
              border: "1px solid rgba(59,130,246,0.25)",
            }}
          >
            <Box
              sx={{
                flexBasis: { xs: "100%", md: "220px" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                onClick={handleStartExam}
                disabled={!Array.isArray(sentences) || sentences.length === 0}
                sx={{
                  fontWeight: 600,
                  paddingX: 3,
                  paddingY: 1.2,
                  backgroundColor: "#4CAF50",
                  color: "#ffffff",
                  '&.Mui-disabled': { backgroundColor: "#C8E6C9", color: "#ffffff" },
                  '&:hover': { backgroundColor: "#43A047" },
                }}
              >
                例文暗記テスト
              </Button>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                backgroundColor: "rgba(255,255,255,0.9)",
                borderRadius: "14px",
                padding: 2,
                overflowX: "auto",
                boxShadow: "inset 0 1px 4px rgba(148,163,184,0.25)",
              }}
            >
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "460px" }}>
                <tbody>
                  {attemptSummaryRows.length === 0 ? (
                    <tr>
                      <td style={{ padding: "12px", color: "#94a3b8", textAlign: "center" }}>まだ記録がありません</td>
                    </tr>
                  ) : (
                    <>
                      <tr>
                        {attemptSummaryRows.map((row) => (
                          <td
                            key={`idx-${row.index}`}
                            style={{ padding: "10px 14px", fontWeight: 600, textAlign: "center", color: "#1e3a8a" }}
                          >
                          ({row.index})
                          </td>
                        ))}
                      </tr>
                      <tr>
                        {attemptSummaryRows.map((row) => (
                          <td
                            key={`res-${row.index}`}
                            style={{ padding: "10px 14px", fontWeight: 600, textAlign: "center", color: row.passed ? "#15803d" : "#94a3b8" }}
                          >
                            {row.passed ? "合格" : ""}
                          </td>
                        ))}
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </Box>
          </Paper>
        </Box>

          {/* カード一覧 */}
          <Box display="flex" flexDirection="column" gap={2}>
            {Array.isArray(sentences) &&
              sentences.map((item, index) => (
                <Paper key={item.id || index} sx={{ padding: 2 }}>
                  {item.sentence_jp && (
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {item.sentence_jp}
                    </Typography>
                  )}
                  <Typography variant="h6" gutterBottom>
                    {item.sentence}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', marginTop: 1 }}>
                    {Array.isArray(item.audio_files) && item.audio_files.map((file, i) => (
                      <React.Fragment key={`${index}_${i}`}>
                        {playingKey === `${index}_${i}` ? (
                          <Button variant="outlined" color="error" onClick={stopAllAudio} sx={{ mr: 1 }}>
                            停止
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={() => handlePlay(index, i)}
                            sx={{ mr: 1 }}
                          >
                            再生
                          </Button>
                        )}
                        <audio
                          style={{ display: "none" }}
                          ref={(el) => (audioRefs.current[`${index}_${i}`] = el)}
                          onEnded={handleEnded}
                        >
                          <source src={`${AUDIO_BASE}/audio_clipped/${file}`} type="audio/mpeg" />
                        </audio>
                      </React.Fragment>
                    ))}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handlePracticeInPage(item)}
                    >
                      発音練習
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => handleNavigateToLesson(item)}
                    >
                      先生と練習
                    </Button>
                  </Box>
                </Paper>
              ))}
          </Box>
        </Container>
      </div>
    );
  }

  // 録音画面の表示
  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <AuthHeader pageTitle="Pronunciation Lab" />
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "calc(100vh - 60px)",
        padding: "20px"
      }}>
        {/* 選択された文を表示 */}
        <div style={{ marginBottom: "30px", textAlign: "center", maxWidth: "800px" }}>
          {selectedSentence.sentence_jp && (
            <h2 style={{ color: "#666", marginBottom: "10px" }}>
              {selectedSentence.sentence_jp}
            </h2>
          )}
          <h2 style={{ color: "#333" }}>
            {selectedSentence.sentence}
          </h2>
        </div>

        {/* カウントダウン表示 */}
        {isCountdown && (
          <div style={{ textAlign: "center" }}>
            <div style={{
              fontSize: "48px",
              color: "#f44336",
              marginBottom: "20px",
              animation: "pulse 1s infinite"
            }}>
              録音開始まで
            </div>
            <div style={{
              fontSize: "72px",
              color: "#FF9800",
              marginBottom: "30px",
              fontWeight: "bold"
            }}>
              {countdownValue}
            </div>
            <button
              onClick={cancelCountdown}
              style={{
                padding: "15px 40px",
                fontSize: "20px",
                backgroundColor: "#f44336",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              キャンセル
            </button>
          </div>
        )}

        {/* 録音ボタン（初期状態） */}
        {!isCountdown && !isRecording && !hasRecorded && !analysisResult && (
          <>
            <button
              onClick={startCountdown}
              style={{
                padding: "20px 60px",
                fontSize: "24px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                marginBottom: "20px"
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = "#45a049"}
              onMouseOut={(e) => e.target.style.backgroundColor = "#4CAF50"}
            >
              録音
            </button>
            <button
              onClick={handleBackToList}
              style={{
                padding: "10px 30px",
                fontSize: "16px",
                backgroundColor: "#757575",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              戻る
            </button>
          </>
        )}

        {/* 録音中の表示 */}
        {isRecording && (
          <div style={{ textAlign: "center" }}>
            <div style={{
              fontSize: "48px",
              color: "#f44336",
              marginBottom: "20px",
              animation: "pulse 1s infinite"
            }}>
              ● 録音中
            </div>
            <div style={{ fontSize: "32px", marginBottom: "30px" }}>
              {recordingTime} / 7秒
            </div>
            <button
              onClick={stopRecording}
              style={{
                padding: "15px 40px",
                fontSize: "20px",
                backgroundColor: "#f44336",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              終了
            </button>
          </div>
        )}

        {/* 分析中の表示 */}
        {isAnalyzing && (
          <div style={{ textAlign: "center", fontSize: "24px", color: "#666" }}>
            分析中...
          </div>
        )}

        {/* エラー表示 */}
        {error && (
          <div style={{
            backgroundColor: "#ffebee",
            color: "#c62828",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "20px",
            maxWidth: "600px"
          }}>
            {error}
          </div>
        )}

        {/* 分析結果の表示 */}
        {analysisResult && !showDetailedView && (
          <div style={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            maxWidth: "900px",
            width: "100%"
          }}>
            {/* 合格/不合格の表示 */}
            {(() => {
              const isPassed = checkPassStatus();
              return (
                <div style={{ textAlign: "center", marginBottom: "40px" }}>
                  <div style={{
                    fontSize: "64px",
                    fontWeight: "bold",
                    color: isPassed ? "#4CAF50" : "#f44336",
                    marginBottom: "20px"
                  }}>
                    {isPassed ? "合格" : "不合格"}
                  </div>
                  <div style={{
                    fontSize: "24px",
                    color: "#666",
                    whiteSpace: "pre-line"
                  }}>
                    {isPassed ? "完璧です！\n次もがんばりましょう！" : "残念！\nお手本を聞いてもう一度挑戦！"}
                  </div>
                </div>
              );
            })()}

            {/* 単語対応分析テーブル（簡易版） */}
            {analysisResult.alignment && analysisResult.alignment.alignments && (
              <div style={{ marginTop: "30px" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    backgroundColor: "#f9f9f9"
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: "#e0e0e0" }}>
                        <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #ccc" }}>例文</th>
                        <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #ccc" }}>発話</th>
                        <th style={{ padding: "12px", textAlign: "center", borderBottom: "2px solid #ccc" }}>評価</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisResult.alignment.alignments
                        .filter(item => !item?.ignored_due_to_proper)
                        .map((item, index) => {
                          const evaluationText = getEvaluationText(item);

                          // 評価に応じた色分け
                          let evaluationColor = "#000";
                          if (evaluationText === "完璧") {
                          evaluationColor = "#4CAF50";
                        } else if (evaluationText === "上手") {
                          evaluationColor = "#8BC34A";
                        } else if (evaluationText === "聞き取りにくいです") {
                          evaluationColor = "#FF9800";
                        } else if (evaluationText === "聞き取れませんでした" || evaluationText === "間違いです") {
                          evaluationColor = "#f44336";
                        } else if (evaluationText === "採点外") {
                          evaluationColor = "#666";
                        }

                        return (
                          <tr key={index} style={{ borderBottom: "1px solid #e0e0e0" }}>
                            <td style={{ padding: "12px" }}>{item.ref_word || "-"}</td>
                            <td style={{ padding: "12px" }}>{item.hyp_word || "-"}</td>
                            <td style={{
                              padding: "12px",
                              textAlign: "center",
                              color: evaluationColor,
                              fontWeight: "bold"
                            }}>
                              {evaluationText}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div style={{ marginTop: "30px", display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
              <button
                onClick={() => setShowDetailedView(true)}
                style={{
                  padding: "15px 40px",
                  fontSize: "18px",
                  backgroundColor: "#9C27B0",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                詳細表示
              </button>
              <button
                onClick={resetRecording}
                style={{
                  padding: "15px 40px",
                  fontSize: "18px",
                  backgroundColor: "#2196F3",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                もう一度
              </button>
              <button
                onClick={handleBackToList}
                style={{
                  padding: "15px 40px",
                  fontSize: "18px",
                  backgroundColor: "#757575",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                一覧に戻る
              </button>
            </div>
          </div>
        )}

        {/* 詳細表示画面 */}
        {analysisResult && showDetailedView && (
          <div style={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            maxWidth: "900px",
            width: "100%"
          }}>
            <h2 style={{ marginBottom: "20px", color: "#333" }}>詳細分析結果</h2>

            <div style={{ marginBottom: "20px" }}>
              <h3 style={{ color: "#555", fontSize: "18px" }}>認識されたテキスト:</h3>
              <p style={{ fontSize: "20px", marginTop: "10px" }}>
                {analysisResult.transcript || "（認識できませんでした）"}
              </p>
              <p style={{ color: "#666", marginTop: "5px" }}>
                全体の信頼度: {analysisResult.confidence ? (analysisResult.confidence * 100).toFixed(2) : 0}%
              </p>
            </div>

            {/* アライメント分析結果のテーブル表示（詳細版） */}
            {analysisResult.alignment && analysisResult.alignment.alignments && (
              <div style={{ marginTop: "30px" }}>
                <h3 style={{ color: "#555", fontSize: "18px", marginBottom: "15px" }}>
                  単語対応分析:
                </h3>
                <div style={{ overflowX: "auto" }}>
                  <table style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    backgroundColor: "#f9f9f9"
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: "#e0e0e0" }}>
                        <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #ccc" }}>例文</th>
                        <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #ccc" }}>発話</th>
                        <th style={{ padding: "12px", textAlign: "center", borderBottom: "2px solid #ccc" }}>発音信頼度</th>
                        <th style={{ padding: "12px", textAlign: "center", borderBottom: "2px solid #ccc" }}>状態（距離比率）</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisResult.alignment.alignments
                        .filter(item => !item?.ignored_due_to_proper)
                        .map((item, index) => {
                        const statusDisplay = {
                          "match": "✓ 完全一致",
                          "error": "△ 不一致",
                          "deletion": "✗ 削除",
                          "insertion": "＋ 挿入"
                        }[item.status] || item.status;

                        const statusColor = {
                          "match": "#4CAF50",
                          "error": "#FF9800",
                          "deletion": "#f44336",
                          "insertion": "#2196F3"
                        }[item.status] || "#000";

                        const distanceRatioText = typeof item.distance_ratio === 'number' && item.distance_ratio >= 0
                          ? `(${Math.round(item.distance_ratio * 100)}%)`
                          : "";

                        // 発話の単語に対応する信頼度を取得
                        const normalizedHyp =
                          typeof item.normalized === "string" && item.normalized
                            ? item.normalized
                            : normalizeWord(item.hyp_word ?? "");
                        let confidence = resolveConfidence(item);

                        // EXCLUDED_WORDSかどうかチェック
                        const isExcluded = normalizedHyp ? EXCLUDED_WORDS.has(normalizedHyp) : false;
                        if (item.is_proper_noun || item.ignored_due_to_proper) {
                          return null;
                        }

                        // 信頼度の色分け
                        let confidenceColor = "#000";
                        if (isExcluded) {
                          confidenceColor = "#BDBDBD"; // うすい灰色
                        } else if (confidence !== null) {
                          if (confidence >= 0.6) {
                            confidenceColor = "#4CAF50"; // 完璧（緑）
                          } else if (confidence >= 0.4) {
                            confidenceColor = "#8BC34A"; // 上手（黄緑）
                          } else {
                            confidenceColor = "#FF9800"; // 聞き取りにくいです（オレンジ）
                          }
                        }

                        return (
                          <tr key={index} style={{ borderBottom: "1px solid #e0e0e0" }}>
                            <td style={{ padding: "12px" }}>{item.ref_word || "-"}</td>
                            <td style={{ padding: "12px" }}>{item.hyp_word || "-"}</td>
                            <td style={{
                              padding: "12px",
                              textAlign: "center",
                              color: confidenceColor,
                              fontWeight: isExcluded ? "normal" : "bold"
                            }}>
                              {confidence !== null ? `${Math.round(confidence * 100)}%` : "-"}
                            </td>
                            <td style={{
                              padding: "12px",
                              textAlign: "center",
                              color: statusColor,
                              fontWeight: "bold"
                            }}>
                              {statusDisplay} {distanceRatioText}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* 統計情報 */}
                {analysisResult.alignment.statistics && (
                  <div style={{
                    marginTop: "20px",
                    padding: "15px",
                    backgroundColor: "#f0f0f0",
                    borderRadius: "8px"
                  }}>
                    <h4 style={{ marginBottom: "10px" }}>統計:</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}>
                      <div>完全一致: {analysisResult.alignment.statistics.matches}</div>
                      <div>不一致: {analysisResult.alignment.statistics.errors}</div>
                      <div>削除: {analysisResult.alignment.statistics.deletions}</div>
                      <div>挿入: {analysisResult.alignment.statistics.insertions}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: "30px", display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
              <button
                onClick={() => setShowDetailedView(false)}
                style={{
                  padding: "15px 40px",
                  fontSize: "18px",
                  backgroundColor: "#9C27B0",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                前画面に戻る
              </button>
              <button
                onClick={resetRecording}
                style={{
                  padding: "15px 40px",
                  fontSize: "18px",
                  backgroundColor: "#2196F3",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                もう一度
              </button>
              <button
                onClick={handleBackToList}
                style={{
                  padding: "15px 40px",
                  fontSize: "18px",
                  backgroundColor: "#757575",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                一覧に戻る
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default PronunciationLabPage;
